import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { makeStyles } from '@material-ui/core/styles';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Divider,
} from '@material-ui/core';
import { listQuestionAnswers, listQuestions, listQuestionGroups } from '../graphql/queries';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(3),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 200,
  },
  instructions: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
}));

const ShowQuestionTree = () => {
  const classes = useStyles();

  const [questionList, setQuestionList] = useState([]);
  const [answerList, setAnswerList] = useState([]);
  const [selectedQuestionGroup, setSelectedQuestionGroup] = useState(null);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [userResponses, setUserResponses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchQuestionGroups();
    fetchAnswerList();
  }, []);

  useEffect(() => {
    if (selectedQuestionGroup) {
      fetchQuestionList(selectedQuestionGroup);
      // Reset previous state
      setSteps([]);
      setCurrentStep(0);
      setUserResponses([]);
    }
  }, [selectedQuestionGroup]);

  const fetchQuestionGroups = async () => {
    setLoading(true);
    let nextToken = null;
    const allQuestionGroups = [];
    
    try {
      do {
        const response = await API.graphql(
          graphqlOperation(listQuestionGroups, { nextToken })
        );
        const qGroupList = response.data?.listQuestionGroups?.items || [];
        allQuestionGroups.push(...qGroupList);
        nextToken = response.data?.listQuestionGroups?.nextToken || null;
      } while (nextToken);
  
      setQuestionGroups(allQuestionGroups);
    } catch (error) {
      console.error("Error fetching question groups:", error);
    } finally {
      setLoading(false);
    }
  };
  

  const fetchQuestionList = async (questionGroupId) => {
    setLoading(true);
    let nextToken = null;
    const allQuestions = [];
    
    try {
      do {
        const response = await API.graphql(
          graphqlOperation(listQuestions, {
            filter: {
              questionsQuestionGroupId: { eq: questionGroupId },
            },
            nextToken,
          })
        );
        const qList = response.data?.listQuestions?.items || [];
        allQuestions.push(...qList);
        nextToken = response.data?.listQuestions?.nextToken || null;
      } while (nextToken);
  
      setQuestionList(allQuestions);
  
      // Start with the first question
      const firstQuestion = allQuestions.find((q) => q.isFirstQuestion === true);
      setCurrentQuestion(firstQuestion);
      setSteps([firstQuestion]);
    } catch (error) {
      console.error("Error fetching question list:", error);
    } finally {
      setLoading(false);
    }
  };
  

  const fetchAnswerList = async () => {
    setLoading(true);
    let nextToken = null;
    const allAnswers = [];
  
    try {
      do {
        const response = await API.graphql(
          graphqlOperation(listQuestionAnswers, { nextToken })
        );
        const aList = response.data?.listQuestionAnswers?.items || [];
        allAnswers.push(...aList);
        nextToken = response.data?.listQuestionAnswers?.nextToken || null;
      } while (nextToken);
  
      setAnswerList(allAnswers);
    } catch (error) {
      console.error("Error fetching answer list:", error);
    } finally {
      setLoading(false);
    }
  };
  

  const handleAnswerClick = (selectedAnswer) => {
    const nextQuestionId = selectedAnswer.nextQuestionID;
    const nextQuestion = questionList.find((q) => q.id === nextQuestionId);

    // Store the current question and selected answer
    setUserResponses((prevResponses) => [
      ...prevResponses,
      {
        question: currentQuestion,
        answer: selectedAnswer,
      },
    ]);

    if (nextQuestion) {
      setSteps((prevSteps) => [...prevSteps, nextQuestion]);
      setCurrentQuestion(nextQuestion);
      setCurrentStep((prevStep) => prevStep + 1);
    } else {
      // Questionnaire is finished
      setCurrentQuestion(null);
    }
  };

  const handleStartNextQuestionGroup = (groupId) => {
    setSelectedQuestionGroup(groupId);
    setSteps([]);
    setCurrentStep(0);
    setUserResponses([]);
  };

  const renderAnswers = () => {
    return answerList
      .filter(
        (answer) => answer.questionAnswersQuestionsId === currentQuestion.id
      )
      .map((answer) => (
        <Button
          key={answer.id}
          variant="contained"
          color="primary"
          onClick={() => handleAnswerClick(answer)}
          style={{ margin: '8px 8px 0 0' }}
        >
          {answer.answer}
        </Button>
      ));
  };

  return (
    <div className={classes.root}>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <FormControl className={classes.formControl}>
            <InputLabel id="question-group-select-label">
              Question Group
            </InputLabel>
            <Select
              labelId="question-group-select-label"
              id="question-group-select"
              value={selectedQuestionGroup || ''}
              onChange={(e) => setSelectedQuestionGroup(e.target.value)}
            >
              {questionGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {currentQuestion ? (
            <>
              <Stepper activeStep={currentStep} orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel>{step.question}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <div>
                <Typography variant="h6" className={classes.instructions}>
                  {currentQuestion.question}
                </Typography>

                {renderAnswers()}
              </div>
            </>
          ) : (
            userResponses.length > 0 && (
              <Summary
                responses={userResponses}
                onStartNextQuestionGroup={handleStartNextQuestionGroup}
              />
            )
          )}
        </>
      )}
    </div>
  );
};

const Summary = ({ responses, onStartNextQuestionGroup }) => {
  const classes = useStyles();
  const nextQuestionGroup = responses
    .map((response) => response.answer.QuestionGroup)
    .find((group) => group);

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        Summary
      </Typography>
      <Grid container spacing={2}>
        {responses.map((response, index) => (
          <Grid item xs={12} key={index}>
            <Card className={classes.card}>
              <CardHeader title={`Question ${index + 1}`} />
              <CardContent>
                <Typography variant="subtitle1" color="textSecondary">
                  {response.question.question}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Your Answer:</strong> {response.answer.answer}
                </Typography>

                {/* Display GuidanceContent */}
                {response.answer.GuidanceContent && (
                  <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="subtitle2">
                      Guidance Content:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {response.answer.GuidanceContent.title}
                    </Typography>
                  </>
                )}

                {/* Display RoadMapItems */}
                {response.answer.RoadMapItems?.length > 0 && (
                  <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="subtitle2">
                      Road Map Items:
                    </Typography>
                    {response.answer.RoadMapItems.map((item) => (
                      <Typography
                        key={item.id}
                        variant="body2"
                        color="textSecondary"
                      >
                        {item.text}
                      </Typography>
                    ))}
                  </>
                )}

                {/* Display Next Question Group */}
                {response.answer.QuestionGroup && (
                  <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="subtitle2">
                      Next Question Group:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {response.answer.QuestionGroup.name}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Offer to start the next question group */}
      {nextQuestionGroup && (
        <div style={{ marginTop: '24px' }}>
          <Typography variant="h6">
            You have a next questionnaire available: {nextQuestionGroup.name}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onStartNextQuestionGroup(nextQuestionGroup.id)}
          >
            Start Next Questionnaire
          </Button>
        </div>
      )}
    </div>
  );
};

export default ShowQuestionTree;
